<ng-container *ngIf="initialized">

  <!-- TM-1000001713: sticky header region — wraps the existing mode/use-case "header" panel
       and stacks the soft-delete warning banner immediately beneath it. Both remain visible
       while the user scrolls long forms. The wrapper itself is always rendered so the sticky
       context exists even when only one of the two children is visible. -->
  <div class="mvs-crud-object-form__sticky-header sticky top-0 z-2 surface-0">

    <!-- PANEL -->


    @if (!forceHideModes) {
      @if (sgUseCaseId) {
        <p-selectButton
          [options]="formModeOptions"
          [(ngModel)]="selectedFormMode"
          (onChange)="onFormModeChange($event.value)"
          optionLabel="label"
          optionValue="value"
          [allowEmpty]="false"
          [dataKey]="'value'"
        >
          <ng-template #item let-item >
            <div class="flex items-center gap-2">
              <span>{{ item.label }}</span>
              <i *ngIf="busyFormMode === item.value" class="pi pi-spinner pi-spin text-xs"></i>
            </div>
          </ng-template>
        </p-selectButton>
      }
    }


    @if (formMode == FormModeEnum.SMART_CONFIG && !sgUseCaseId) {

      Use Case:

      @if (!sgUseCaseId) {
        <p-select
          [options]="sgUseCases"
          optionValue="id"
          optionLabel="name"
          [placeholder]="'Select a use case'"
          [(ngModel)]="selectedUseCaseId"
          (onChange)="onUseCaseChange($event)">
        </p-select>
      }


    }

    <!-- TM-1000001713: Soft-delete warning banner — moved from the footer to the top of the form,
         directly below the mode/use-case header, and pinned via the surrounding sticky wrapper.
         Visibility condition is unchanged: only rendered when softDeletedNotice is set
         (i.e. entityStatus === EntityStatusEnum.deleted). The banner uses severity="warn" with the
         Rückgängig button on the right. -->
    @if (softDeletedNotice) {
      <div class="w-full pb-2">
        <p-message severity="warn" styleClass="w-full">
          <div class="flex align-items-center justify-content-between gap-2 w-full">
            <div class="flex align-items-center gap-2">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ softDeletedNotice.message }}</span>
            </div>
            <p-button
              icon="pi pi-undo"
              label="Rückgängig"
              severity="contrast"
              size="small"
              [text]="true"
              styleClass="gap-1"
              pTooltip="Rückgängig"
              tooltipPosition="top"
              (click)="onUndoSoftDelete.emit(softDeletedNotice.id)">
            </p-button>
          </div>
        </p-message>
      </div>
    }

  </div>


  <!-- CRUD FORM -->
  <!-- TM-1000001550: Use root form objectType so confirmable fields on 1:1 sub-entities send the correct dotted activityField -->
  <!-- TM-1000001714: when soft-deleted, grey out + disable pointer events on the entire form so fields are clearly inert -->
  <div [style.pointer-events]="softDeletedNotice ? 'none' : null"
       [style.opacity]="softDeletedNotice ? '0.55' : null"
       [style.user-select]="softDeletedNotice ? 'none' : null">
    <mvs-form
      [formGroup]="formGroup"
      [subForm]="false"
      [renderRevision]="formGroup?.renderRevision"
      [readonly]="readonly || !!softDeletedNotice"
      [sgUseCaseId]="selectedUseCaseId"
      [disabled]="disabled || !!softDeletedNotice"

      [templates]="templates"
      [showAudit]="showAudit"
      [uniqueId]="uniqueId"
      [formMode]="formMode"
      [mouseEvent]="mouseEvent"
      [rootObjectType]="objectType"
      (onChangeFormField)="handleChangeFormField($event)"
      (lifecycleEvents)="handleLifecycleEvents($event)"
      (onConfirmationFieldRefresh)="handleConfirmationFieldRefresh($event)"
      (onFormTargetFocus)="handleFormTargetFocus($event)"

    ></mvs-form>
  </div>


  <!-- ACTION BAR -->

  @if (formMode != FormModeEnum.SMART_CONFIG) {

    <!-- TM-1000001713: When soft-deleted, the entire footer is hidden — info/history icons,
         delete button, save/anlegen buttons, custom buttons and back button. The user can only
         restore via the Rückgängig button inside the sticky warning banner above. The banner
         itself was relocated from this footer to the sticky header region (see top of template).
         Pre-existing TM-1000001714 disable behaviour (pointer-events:none, opacity, readonly,
         disabled) on the form body wrapper above is unchanged. -->
    @if (!softDeletedNotice) {

      <!-- hideActionBar: the caller owns every action, so the bar (and its padding) is not rendered at all. -->
      <div class="flex justify-content-between flex-wrap pt-4 pr-3" *ngIf="initialized && !hideActionBar">


        <div class="flex-initial flex left-0 align-items-center gap-2">
          <p-button *ngIf="!createMode && !hideAuditAction" styleClass="p-button-text" [disabled]="busy || disabled"
                    icon="fa-regular fa-circle-info" (click)="toggleAudit($event)"></p-button>
          <p-button icon="pi pi-history" styleClass="p-button-text" *ngIf="!createMode && !hideHistoryAction"
                    [disabled]="!complete || busy || disabled" (onClick)="onShowHistory($event)"
                    pTooltip="Änderungshistorie"></p-button>

          <p-button *ngIf="!createMode && !removeDelete && !hideDefaultActions" styleClass="p-button-danger p-button-text"
                    [disabled]="busy || disabled"
                    icon="pi pi-trash" (click)="onAction('delete')"></p-button>

        </div>
        <div class="flex-initial flex right-100 gap-3">

          <ng-container *ngIf="formFooterCustomButtons?.length">
            <ng-container *ngFor="let button of formFooterCustomButtons">
              <p-button
                *ngIf="button.display"
                [icon]="button.icon"
                [label]="button.label"
                [pTooltip]="button.tooltip"
                [disabled]="
                  button.disabled ||
                  busy ||
                  disabled"
                tooltipPosition="bottom"
                [styleClass]="button.styleClass"
                (click)="handleCustomButtonClick(button)"
              ></p-button>
            </ng-container>
          </ng-container>

          <p-button icon="pi pi-arrow-left" (click)="onBack()" styleClass="p-button-outlined" label="Back"
                    *ngIf="enableBack"></p-button>
          <!--    Ticket # 1000000905-->
          <!--      <p-button styleClass="p-button-success" *ngIf="createMode"-->
          <!--                [disabled]="!complete || busy || disabled || formState !== 'VALID'"-->
          <!--                icon="pi pi-check" (click)="onAction('create')" label="Anlegen"></p-button>-->

          <p-button styleClass="p-button-success" *ngIf="createMode && !hideDefaultActions"
                    [disabled]="!complete || busy || disabled || !formGroup.valid"
                    icon="pi pi-check" (click)="onAction('create')" label="Anlegen"></p-button>

          <p-button *ngIf="!createMode && !hideDefaultActions" [disabled]="!complete || busy || disabled" icon="pi pi-check-square"
                    (click)="onAction('save')" label="Speichern"></p-button>


          <ng-container *ngIf="formFooterButtons">
            <ng-container *ngFor="let button of formFooterButtons">
              <p-button
                *ngIf="button.display && (createMode ? button.action === 'create' : button.action === 'update')"
                [icon]="button.icon"
                [label]="button.label"
                [pTooltip]="button.tooltip"
                [disabled]="!complete || busy || disabled || (button.action === 'create' ? !formGroup.valid : false)"
                tooltipPosition="bottom"
                [styleClass]="createMode ? 'p-button-success' : ''"
                (click)="handleButtonClick(button)"
              ></p-button>
            </ng-container>
          </ng-container>
        </div>


        <!--
        <div>
            <div>Complete {{complete}}</div>
            <div>busy {{busy}}</div>
            <div>disabled {{disabled}}</div>
            <div>formState {{formState}}</div>
        </div>
        -->

      </div>

    }
  }


</ng-container>
